// In-memory fixtures + a request resolver for demo mode.
//
// resolveDemoRequest(endpoint, method, body) maps an API path to a realistic
// dummy payload shaped exactly like the real backend response (see api.ts
// interfaces and the per-page field map). Every page in the dashboard reads
// `response.data`, so the resolver returns the bare `data` object and the API
// service wraps it as { data }.

import type { User, AuthResponse } from '../services/api'
import { getDemoRole, DEMO_TOKEN } from './demoMode'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const now = new Date()
const daysFromNow = (d: number) => new Date(now.getTime() + d * 86400000).toISOString()
const minsAgo = (m: number) => new Date(now.getTime() - m * 60000).toISOString()

// ---------------------------------------------------------------------------
// Demo user / auth
// ---------------------------------------------------------------------------

const ROLE_NAMES: Record<User['role'], { first: string; last: string }> = {
  EVENT_ORGANIZER: { first: 'Jordan', last: 'Riley' },
  COACH: { first: 'Marcus', last: 'Bennett' },
  SCOUT: { first: 'Dana', last: 'Holt' },
  ATHLETE: { first: 'Marcus', last: 'Washington' },
  PARENT: { first: 'Patricia', last: 'Washington' },
  ADMIN: { first: 'Alex', last: 'Morgan' },
}

export function buildDemoUser(role: User['role'] = getDemoRole()): User {
  const name = ROLE_NAMES[role] || ROLE_NAMES.EVENT_ORGANIZER
  return {
    id: `demo-${role.toLowerCase()}`,
    email: `${role.toLowerCase().replace('_', '.')}@demo.ecombine.app`,
    role,
    profile: {
      id: `demo-profile-${role.toLowerCase()}`,
      firstName: name.first,
      lastName: name.last,
      phoneNumber: '(555) 010-2026',
    },
    createdAt: daysFromNow(-420),
    updatedAt: daysFromNow(-2),
  }
}

function buildAuthResponse(): AuthResponse {
  return { user: buildDemoUser(), token: DEMO_TOKEN, refreshToken: `${DEMO_TOKEN}-refresh` }
}

// ---------------------------------------------------------------------------
// Core dataset
// ---------------------------------------------------------------------------

const FOOTBALL_DRILLS = [
  {
    id: 'drl-40',
    name: '40-Yard Dash',
    description: 'Straight-line speed with 10/20-yd splits',
    unit: 's',
    sport: 'FOOTBALL',
  },
  {
    id: 'drl-3cone',
    name: '3-Cone Drill',
    description: 'Change-of-direction agility',
    unit: 's',
    sport: 'FOOTBALL',
  },
  {
    id: 'drl-shuttle',
    name: '20-Yard Shuttle',
    description: 'Lateral quickness',
    unit: 's',
    sport: 'FOOTBALL',
  },
  {
    id: 'drl-vert',
    name: 'Vertical Jump',
    description: 'Lower-body explosiveness',
    unit: 'in',
    sport: 'FOOTBALL',
  },
  {
    id: 'drl-broad',
    name: 'Broad Jump',
    description: 'Horizontal power',
    unit: 'in',
    sport: 'FOOTBALL',
  },
]

const BASEBALL_DRILLS = [
  {
    id: 'drl-60',
    name: '60-Yard Dash',
    description: 'Baseball straight-line speed',
    unit: 's',
    sport: 'BASEBALL',
  },
  {
    id: 'drl-pop',
    name: 'Catcher Pop Time',
    description: 'Exchange + throw to 2nd',
    unit: 's',
    sport: 'BASEBALL',
  },
  {
    id: 'drl-exit',
    name: 'Exit Velocity',
    description: 'Bat-to-ball power',
    unit: 'mph',
    sport: 'BASEBALL',
  },
  {
    id: 'drl-arm',
    name: 'Outfield Arm Strength',
    description: 'Throw velocity',
    unit: 'mph',
    sport: 'BASEBALL',
  },
]

interface DemoAthlete {
  id: string
  firstName: string
  lastName: string
  position: string
  sport: string
  school: string
  graduationYear: number
  state: string
  height: string
  weight: number
  topPercentile: number
}

const ATHLETES: DemoAthlete[] = [
  {
    id: 'ath-1',
    firstName: 'Marcus',
    lastName: 'Washington',
    position: 'WR',
    sport: 'FOOTBALL',
    school: 'Westfield High',
    graduationYear: 2026,
    state: 'TX',
    height: '6\'1"',
    weight: 185,
    topPercentile: 99,
  },
  {
    id: 'ath-2',
    firstName: 'DeAndre',
    lastName: 'Collins',
    position: 'RB',
    sport: 'FOOTBALL',
    school: 'Lincoln Prep',
    graduationYear: 2026,
    state: 'TX',
    height: '5\'10"',
    weight: 195,
    topPercentile: 96,
  },
  {
    id: 'ath-3',
    firstName: 'Tyler',
    lastName: 'Jameson',
    position: 'WR',
    sport: 'FOOTBALL',
    school: 'Westfield High',
    graduationYear: 2027,
    state: 'TX',
    height: '6\'0"',
    weight: 178,
    topPercentile: 91,
  },
  {
    id: 'ath-4',
    firstName: 'Jordan',
    lastName: 'Lee',
    position: 'QB',
    sport: 'FOOTBALL',
    school: 'Northgate Academy',
    graduationYear: 2026,
    state: 'OK',
    height: '6\'3"',
    weight: 210,
    topPercentile: 88,
  },
  {
    id: 'ath-5',
    firstName: 'Cameron',
    lastName: 'Diaz',
    position: 'CB',
    sport: 'FOOTBALL',
    school: 'Lincoln Prep',
    graduationYear: 2027,
    state: 'TX',
    height: '5\'11"',
    weight: 175,
    topPercentile: 84,
  },
  {
    id: 'ath-6',
    firstName: 'Isaiah',
    lastName: 'Brooks',
    position: 'LB',
    sport: 'FOOTBALL',
    school: 'Riverside High',
    graduationYear: 2026,
    state: 'LA',
    height: '6\'2"',
    weight: 222,
    topPercentile: 81,
  },
  {
    id: 'ath-7',
    firstName: 'Mason',
    lastName: 'Carter',
    position: 'C',
    sport: 'BASEBALL',
    school: 'Westfield High',
    graduationYear: 2026,
    state: 'TX',
    height: '6\'0"',
    weight: 190,
    topPercentile: 94,
  },
  {
    id: 'ath-8',
    firstName: 'Eli',
    lastName: 'Rodriguez',
    position: 'SS',
    sport: 'BASEBALL',
    school: 'Northgate Academy',
    graduationYear: 2027,
    state: 'OK',
    height: '5\'11"',
    weight: 172,
    topPercentile: 89,
  },
  {
    id: 'ath-9',
    firstName: 'Noah',
    lastName: 'Bennett',
    position: 'CF',
    sport: 'BASEBALL',
    school: 'Riverside High',
    graduationYear: 2026,
    state: 'LA',
    height: '6\'1"',
    weight: 183,
    topPercentile: 86,
  },
  {
    id: 'ath-10',
    firstName: 'Aiden',
    lastName: 'Foster',
    position: 'P',
    sport: 'BASEBALL',
    school: 'Lincoln Prep',
    graduationYear: 2026,
    state: 'TX',
    height: '6\'4"',
    weight: 205,
    topPercentile: 92,
  },
  {
    id: 'ath-11',
    firstName: 'Lucas',
    lastName: 'Nguyen',
    position: 'S',
    sport: 'FOOTBALL',
    school: 'Riverside High',
    graduationYear: 2027,
    state: 'LA',
    height: '6\'0"',
    weight: 188,
    topPercentile: 78,
  },
  {
    id: 'ath-12',
    firstName: 'Gabriel',
    lastName: 'Santos',
    position: '2B',
    sport: 'BASEBALL',
    school: 'Westfield High',
    graduationYear: 2027,
    state: 'TX',
    height: '5\'9"',
    weight: 165,
    topPercentile: 75,
  },
]

const athleteName = (a: DemoAthlete) => `${a.firstName} ${a.lastName}`

function participantsFor(ids: string[], statuses: string[] = []) {
  return ids.map((id, i) => {
    const a = ATHLETES.find(x => x.id === id)!
    return {
      id: `part-${id}`,
      status:
        statuses[i] || (i % 4 === 0 ? 'REGISTERED' : i % 3 === 0 ? 'COMPLETED' : 'CHECKED_IN'),
      registeredAt: daysFromNow(-7),
      checkedInAt: i % 4 === 0 ? undefined : minsAgo(120 - i * 5),
      athlete: {
        id: a.id,
        profile: { firstName: a.firstName, lastName: a.lastName, position: a.position },
      },
      team: { id: 'team-1', name: `${a.school} Varsity` },
      school: { id: 'sch-1', name: a.school },
    }
  })
}

const EVENTS = [
  {
    id: 'evt-spring',
    name: 'Spring Showcase 2026 — QB/WR Session',
    description: 'Premier spring evaluation for skill-position prospects across the district.',
    sport: 'FOOTBALL',
    startDate: daysFromNow(0),
    endDate: daysFromNow(0),
    location: 'Westfield High School',
    venue: 'Memorial Stadium',
    status: 'ACTIVE' as const,
    maxParticipants: 60,
    registrationDeadline: daysFromNow(-2),
    participantCount: 23,
    district: { id: 'dst-1', name: 'Northside ISD' },
    school: { id: 'sch-1', name: 'Westfield High' },
    program: { id: 'prg-1', name: 'Varsity Football', sport: 'FOOTBALL' },
    drills: FOOTBALL_DRILLS,
    participants: participantsFor(['ath-1', 'ath-2', 'ath-3', 'ath-4', 'ath-5', 'ath-6']),
  },
  {
    id: 'evt-elite',
    name: 'Elite Prospect Camp — Football',
    description: 'Invitation-only camp for top-ranked recruits.',
    sport: 'FOOTBALL',
    startDate: daysFromNow(9),
    endDate: daysFromNow(10),
    location: 'Northgate Academy',
    venue: 'Falcon Field',
    status: 'SCHEDULED' as const,
    maxParticipants: 40,
    registrationDeadline: daysFromNow(5),
    participantCount: 31,
    district: { id: 'dst-1', name: 'Northside ISD' },
    school: { id: 'sch-2', name: 'Northgate Academy' },
    program: { id: 'prg-2', name: 'Varsity Football', sport: 'FOOTBALL' },
    drills: FOOTBALL_DRILLS,
    participants: participantsFor(['ath-4', 'ath-5', 'ath-6', 'ath-11']),
  },
  {
    id: 'evt-baseball',
    name: 'Diamond Combine 2026',
    description: 'Regional baseball combine — speed, pop time, and exit velocity.',
    sport: 'BASEBALL',
    startDate: daysFromNow(16),
    endDate: daysFromNow(16),
    location: 'Riverside High School',
    venue: 'Riverside Ballpark',
    status: 'SCHEDULED' as const,
    maxParticipants: 50,
    registrationDeadline: daysFromNow(12),
    participantCount: 27,
    district: { id: 'dst-1', name: 'Northside ISD' },
    school: { id: 'sch-3', name: 'Riverside High' },
    program: { id: 'prg-3', name: 'Varsity Baseball', sport: 'BASEBALL' },
    drills: BASEBALL_DRILLS,
    participants: participantsFor(['ath-7', 'ath-8', 'ath-9', 'ath-10', 'ath-12']),
  },
  {
    id: 'evt-fall',
    name: 'Fall Combine 2025 — All Positions',
    description: 'End-of-season evaluation across the program.',
    sport: 'FOOTBALL',
    startDate: daysFromNow(-120),
    endDate: daysFromNow(-120),
    location: 'Westfield High School',
    venue: 'Memorial Stadium',
    status: 'COMPLETED' as const,
    maxParticipants: 80,
    registrationDeadline: daysFromNow(-127),
    participantCount: 72,
    district: { id: 'dst-1', name: 'Northside ISD' },
    school: { id: 'sch-1', name: 'Westfield High' },
    program: { id: 'prg-1', name: 'Varsity Football', sport: 'FOOTBALL' },
    drills: FOOTBALL_DRILLS,
    participants: participantsFor(['ath-1', 'ath-2', 'ath-3']),
  },
  {
    id: 'evt-winter',
    name: 'Winter Skills Clinic (Draft)',
    description: 'Planning draft — not yet published.',
    sport: 'FOOTBALL',
    startDate: daysFromNow(45),
    endDate: daysFromNow(45),
    location: 'Lincoln Prep',
    venue: 'TBD',
    status: 'DRAFT' as const,
    maxParticipants: 30,
    registrationDeadline: daysFromNow(40),
    participantCount: 0,
    district: { id: 'dst-1', name: 'Northside ISD' },
    school: { id: 'sch-4', name: 'Lincoln Prep' },
    program: { id: 'prg-4', name: 'Varsity Football', sport: 'FOOTBALL' },
    drills: FOOTBALL_DRILLS,
    participants: [],
  },
]

const eventsList = {
  events: EVENTS,
  pagination: { page: 1, limit: 20, total: EVENTS.length, pages: 1 },
}

// ---------------------------------------------------------------------------
// Analytics fixtures
// ---------------------------------------------------------------------------

function eventAnalytics(eventId: string) {
  const ev = EVENTS.find(e => e.id === eventId) || EVENTS[0]
  return {
    event: {
      id: ev.id,
      name: ev.name,
      startDate: ev.startDate,
      location: ev.location,
      status: ev.status,
    },
    participation: { registered: 23, checkedIn: 21, completed: 18, noShow: 2, completionRate: 78 },
    drillStats: [
      {
        drill: { id: 'drl-40', name: '40-Yard Dash', unit: 's' },
        totalResults: 18,
        averageValue: 4.62,
        bestValue: 4.38,
        medianValue: 4.59,
        standardDeviation: 0.14,
      },
      {
        drill: { id: 'drl-vert', name: 'Vertical Jump', unit: 'in' },
        totalResults: 17,
        averageValue: 33.4,
        bestValue: 41.0,
        medianValue: 33.0,
        standardDeviation: 3.8,
      },
      {
        drill: { id: 'drl-3cone', name: '3-Cone Drill', unit: 's' },
        totalResults: 15,
        averageValue: 7.12,
        bestValue: 6.71,
        medianValue: 7.08,
        standardDeviation: 0.22,
      },
    ],
    positionBreakdown: [
      { position: 'WR', count: 7, percentage: 30 },
      { position: 'RB', count: 5, percentage: 22 },
      { position: 'QB', count: 4, percentage: 17 },
      { position: 'CB', count: 4, percentage: 17 },
      { position: 'LB', count: 3, percentage: 14 },
    ],
    schoolBreakdown: [
      { school: { id: 'sch-1', name: 'Westfield High' }, participantCount: 9 },
      { school: { id: 'sch-2', name: 'Northgate Academy' }, participantCount: 7 },
      { school: { id: 'sch-3', name: 'Riverside High' }, participantCount: 7 },
    ],
  }
}

function organizationAnalytics(orgType: string, orgId: string) {
  return {
    organization: { id: orgId, name: 'Northside ISD', type: orgType },
    summary: {
      totalAthletes: 248,
      totalEvents: 12,
      totalResults: 3140,
      averagePerformance: 81,
    },
    performanceByPosition: [
      {
        position: 'WR',
        athleteCount: 38,
        averagePercentile: 76,
        topPerformer: { id: 'ath-1', name: 'Marcus Washington', percentile: 99 },
      },
      {
        position: 'RB',
        athleteCount: 31,
        averagePercentile: 72,
        topPerformer: { id: 'ath-2', name: 'DeAndre Collins', percentile: 96 },
      },
      {
        position: 'QB',
        athleteCount: 22,
        averagePercentile: 70,
        topPerformer: { id: 'ath-4', name: 'Jordan Lee', percentile: 88 },
      },
    ],
    eventHistory: EVENTS.filter(e => e.status === 'COMPLETED' || e.status === 'ACTIVE').map(e => ({
      event: { id: e.id, name: e.name, date: e.startDate },
      participantCount: e.participantCount,
      averagePerformance: 78,
    })),
  }
}

/**
 * `GET /api/analytics/coach/overview` (backend/src/routes/analytics.ts):
 * `{ schoolRanking, positionGroups, athletes }` and nothing else. The old
 * fixture also served `topPerformers`, `trainingGoals`, `upcomingEvents` and
 * `stats`, which made the demo show panels the live product could never fill
 * (#160).
 */
function coachOverview() {
  const roster = ATHLETES.slice(0, 6).map((a, i) => ({
    id: a.id,
    name: athleteName(a),
    position: a.position,
    overallPercentile: i === 5 ? null : a.topPercentile,
    drills:
      i === 5
        ? []
        : [
            {
              drillName: '40-Yard Dash',
              bestValue: Number((4.38 + i * 0.05).toFixed(2)),
              percentile: a.topPercentile,
            },
            {
              drillName: 'Vertical Jump',
              bestValue: 38 - i,
              percentile: Math.max(a.topPercentile - 6, 1),
            },
          ],
  }))
  const positions = [...new Set(roster.map(r => r.position))]
  return {
    schoolRanking: {
      schoolName: 'Westfield High',
      rank: null,
      totalSchools: null,
      compositeScore: 91.4,
    },
    positionGroups: positions.map(position => {
      const members = roster.filter(r => r.position === position)
      const ranked = members.filter(m => m.overallPercentile !== null)
      return {
        position,
        athleteCount: members.length,
        avgPercentile: ranked.length
          ? ranked.reduce((acc, m) => acc + (m.overallPercentile as number), 0) / ranked.length
          : null,
        drillBreakdown: [],
      }
    }),
    athletes: roster,
  }
}

// ---------------------------------------------------------------------------
// Results fixtures
// ---------------------------------------------------------------------------

function resultsList() {
  const results = ATHLETES.slice(0, 8).map((a, i) => ({
    id: `res-${i}`,
    value:
      a.sport === 'FOOTBALL'
        ? Number((4.38 + i * 0.04).toFixed(2))
        : Number((6.6 + i * 0.05).toFixed(2)),
    unit: 's',
    verified: i % 3 !== 0,
    recordedAt: minsAgo(i * 7 + 3),
    percentileRank: a.topPercentile,
    athlete: {
      id: a.id,
      profile: { firstName: a.firstName, lastName: a.lastName, position: a.position },
    },
    drill: a.sport === 'FOOTBALL' ? FOOTBALL_DRILLS[0] : BASEBALL_DRILLS[0],
    event: { id: 'evt-spring', name: 'Spring Showcase 2026' },
  }))
  return { results, pagination: { page: 1, limit: 20, total: results.length, pages: 1 } }
}

// ---------------------------------------------------------------------------
// Scout fixtures
// ---------------------------------------------------------------------------

/**
 * `GET /api/scout/search` (backend/src/routes/scout-search.ts:143-151).
 *
 * The route returns a `positions[]` array and NO `state` — it accepts `state`
 * as a filter but never echoes it back. This fixture previously returned a
 * scalar `position` and a `state`, mirroring the frontend's interface rather
 * than the API, which is why the demo looked correct while the live page
 * rendered `undefined` in both columns (#159).
 */
function scoutSearch() {
  const athletes = ATHLETES.map((a, index) => ({
    id: a.id,
    firstName: a.firstName,
    lastName: a.lastName,
    school: a.school,
    graduationYear: a.graduationYear,
    positions: [{ sport: a.sport, position: a.position, isPrimary: true }],
    height: null,
    weight: a.weight,
    sport: a.sport,
    topPercentile: a.topPercentile,
    recentEventCount: (index % 4) + 1,
  }))

  return {
    athletes,
    pagination: { page: 1, limit: 20, total: athletes.length, pages: 1 },
  }
}

/**
 * `GET /api/scout/watchlist` (backend/src/routes/scout-search.ts:234-257).
 *
 * The route returns `{ watchlist: [...] }`, each entry being the ScoutWatchlist
 * row plus a nested `athlete`. The old fixture returned `{ athletes: [{id}] }`,
 * matching a client bug rather than the route.
 */
function scoutWatchlist() {
  return {
    watchlist: [ATHLETES[1], ATHLETES[6] ?? ATHLETES[2]].map((a, index) => ({
      id: `wl-${index + 1}`,
      scoutId: 'demo-scout',
      athleteId: a.id,
      notes: index === 0 ? 'Explosive first step; verify 40 time at the next combine.' : null,
      createdAt: daysFromNow(-30 + index * 7),
      updatedAt: daysFromNow(-30 + index * 7),
      athlete: {
        id: a.id,
        firstName: a.firstName,
        lastName: a.lastName,
        school: a.school,
        graduationYear: a.graduationYear,
        positions: [{ sport: a.sport, position: a.position, isPrimary: true }],
        height: null,
        weight: a.weight,
      },
    })),
  }
}

/**
 * `GET /api/scout/compare` (backend/src/routes/scout-search.ts:438).
 *
 * Returns `{ comparison: [{ profile, drillBests }] }`, where `drillBests` is
 * keyed by drill id. The old fixture returned `{ athletes: [...] }`, which the
 * page could not read — so it fell through to a `Math.random()` branch and
 * showed fabricated measurements (#159).
 */
function scoutCompare(ids: string[]) {
  const chosen = ids
    .map(id => ATHLETES.find(a => a.id === id))
    .filter((a): a is (typeof ATHLETES)[number] => Boolean(a))
  const pool = chosen.length >= 2 ? chosen : ATHLETES.slice(0, 2)

  return {
    comparison: pool.map((a, index) => ({
      profile: {
        id: a.id,
        firstName: a.firstName,
        lastName: a.lastName,
        school: a.school,
        graduationYear: a.graduationYear,
        positions: [{ sport: a.sport, position: a.position, isPrimary: true }],
        height: null,
        weight: a.weight,
      },
      drillBests: {
        'drill-40': {
          drillName: '40 Yard Dash',
          bestValue: 4.42 + index * 0.06,
          unit: 'seconds',
          percentile: a.topPercentile,
        },
        'drill-vert': {
          drillName: 'Vertical Jump',
          bestValue: 36 - index * 1.5,
          unit: 'inches',
          percentile: a.topPercentile - 4,
        },
      },
    })),
  }
}

/**
 * `GET /api/athletes` (backend/src/routes/athletes.ts:41-90 response schema).
 *
 * Note there is NO performance or percentile data in that schema — this fixture
 * must not invent any, which is why the Athletes page dropped its performance
 * tiles (#159). Nor any date of birth or phone number: the list stopped
 * returning those to every role in #162.
 */
function athletesList() {
  const athletes = ATHLETES.map((a, index) => ({
    id: a.id,
    // The first athlete is the signed-in demo athlete, so the athlete dashboard
    // can resolve itself the same way it does live.
    userId: index === 0 ? 'demo-athlete' : `user-${a.id}`,
    sport: a.sport,
    position: a.position,
    graduationYear: a.graduationYear,
    profile: {
      firstName: a.firstName,
      lastName: a.lastName,
    },
    metrics: { weight: a.weight },
  }))

  return {
    athletes,
    pagination: { page: 1, limit: 100, total: athletes.length, pages: 1 },
  }
}

/**
 * `GET /api/athletes/me` — the caller's own athlete record (backend/src/routes/
 * athletes.ts). Takes no id; resolves from the token. The demo athlete is
 * `ATHLETES[0]`, matching the `userId: 'demo-athlete'` in `athletesList()`.
 */
function myAthlete() {
  const a = ATHLETES[0]
  return {
    id: a.id,
    userId: 'demo-athlete',
    sport: a.sport,
    position: a.position,
    graduationYear: a.graduationYear,
    school: a.school,
    profile: { firstName: a.firstName, lastName: a.lastName },
    metrics: { weight: a.weight },
  }
}

/**
 * `GET /api/analytics/athletes/:athleteId`
 * (backend/src/routes/analytics.ts response schema at the `/athletes/:athleteId`
 * route). Every key below is declared there; Fastify strips anything else, so
 * adding a field here that the route does not return would be a wish, not a
 * fixture.
 */
function athleteAnalytics(athleteId: string) {
  const a = ATHLETES.find(x => x.id === athleteId) || ATHLETES[0]

  return {
    athlete: {
      id: a.id,
      profile: {
        firstName: a.firstName,
        lastName: a.lastName,
        position: a.position,
      },
    },
    performanceSummary: {
      totalEvents: 4,
      totalResults: 17,
      averageValue: 4.61,
      bestValue: 4.42,
      improvementTrend: -0.19,
    },
    drillPerformance: [
      {
        drill: { id: 'drill-40', name: '40 Yard Dash', unit: 'seconds' },
        results: [
          { value: 4.68, percentile: 74, eventName: 'Fall Combine', date: daysFromNow(-320) },
          { value: 4.55, percentile: 84, eventName: 'Winter Showcase', date: daysFromNow(-180) },
          {
            value: 4.42,
            percentile: a.topPercentile,
            eventName: 'Regional Combine',
            date: daysFromNow(-40),
          },
        ],
        personalBest: 4.42,
        averageValue: 4.55,
        trend: -0.26,
      },
      {
        drill: { id: 'drill-vert', name: 'Vertical Jump', unit: 'inches' },
        results: [
          { value: 33.5, percentile: 78, eventName: 'Winter Showcase', date: daysFromNow(-180) },
          { value: 35, percentile: 86, eventName: 'Regional Combine', date: daysFromNow(-40) },
        ],
        personalBest: 35,
        averageValue: 34.25,
        trend: 1.5,
      },
    ],
    positionRanking: {
      position: a.position,
      rank: 6,
      totalAthletes: 140,
      percentile: a.topPercentile,
    },
  }
}

/**
 * `GET /api/monitoring/health` — `HealthStatusSchema`
 * (backend/src/routes/monitoring.ts:44-67).
 *
 * Deliberately no `responseTime`: the handler computes one, but the schema does
 * not declare it, so Fastify strips it and the real client never sees it.
 */
function systemHealth() {
  return {
    status: 'healthy',
    timestamp: new Date().toISOString(),
    uptime: 864_000,
    services: { database: 'connected', redis: 'connected', api: 'ok' },
    system: {
      memory: { used: 512, total: 2048, percentage: 25 },
      cpu: { usage: 6 },
    },
    errors: { total: 3, recent: 0 },
  }
}

// ---------------------------------------------------------------------------
// Parent fixtures
// ---------------------------------------------------------------------------

/**
 * `GET /api/consent/my-children` (backend/src/routes/consent.ts): a bare array
 * of consent rows, each carrying the athlete. Note `id` is the CONSENT's id —
 * the dashboard must use `athlete.id` for anything athlete-scoped. This fixture
 * used to be `{ children: [{ name, grade, gpa, ... }] }`, a shape the API has
 * never returned, which is how the live dashboard's breakage hid (#160).
 */
function myChildren() {
  return ['ath-1', 'ath-3'].map((athleteId, i) => {
    const a = ATHLETES.find(x => x.id === athleteId)!
    return {
      id: `consent-${i + 1}`,
      consentType: 'FULL_CONTROL',
      grantedAt: daysFromNow(-200),
      athlete: {
        id: a.id,
        firstName: a.firstName,
        lastName: a.lastName,
        school: a.school,
        positions: [{ sport: a.sport, position: a.position, isPrimary: true }],
      },
    }
  })
}

/**
 * `GET /api/athletes/:id/performance` (backend/src/routes/athletes.ts):
 * `{ athleteId, performance: [...], summary }`. Newest first. There is no
 * "improvement", "personal record" or "training consistency" in the API, so
 * none here either (#160).
 */
function athletePerformance(athleteId: string) {
  const rows = [
    { drill: '40-Yard Dash', value: 4.38, unit: 'SECONDS', percentile: 99, days: -1 },
    { drill: 'Vertical Jump', value: 38.5, unit: 'INCHES', percentile: 92, days: -1 },
    { drill: '3-Cone Drill', value: 6.81, unit: 'SECONDS', percentile: 90, days: -14 },
    { drill: 'Broad Jump', value: 124, unit: 'INCHES', percentile: 87, days: -14 },
    { drill: '20-Yard Shuttle', value: 4.21, unit: 'SECONDS', percentile: null, days: -40 },
  ]
  const ranked = rows.map(r => r.percentile).filter((p): p is number => p !== null)
  return {
    athleteId,
    performance: rows.map((r, i) => ({
      id: `perf-${i + 1}`,
      drill: r.drill,
      value: r.value,
      unit: r.unit,
      percentile: r.percentile,
      event: r.days > -10 ? 'North Texas Fall Combine' : 'Metroplex Spring Showcase',
      date: daysFromNow(r.days),
      verified: true,
    })),
    summary: {
      totalResults: rows.length,
      averagePercentile: ranked.reduce((a, b) => a + b, 0) / ranked.length,
      recentTrend: 'improving',
    },
  }
}

// ---------------------------------------------------------------------------
// Recruiting profile fixtures
// ---------------------------------------------------------------------------

/** `'6\'1"'` -> 73. The demo athletes carry a display string; the route sends inches. */
function demoHeightInches(height: string | null | undefined): number | null {
  if (!height) {
    return null
  }
  const match = height.match(/(\d+)'\s*(\d+)?/)
  if (!match) {
    return null
  }
  return Number(match[1]) * 12 + Number(match[2] || 0)
}

/** Best result per drill, as the profile routes compute it from drillResults. */
function demoCombineDrills(sport: string) {
  return sport === 'FOOTBALL'
    ? [
        { category: 'SPEED', name: '40-Yard Dash', bestValue: 4.38, percentile: 99 },
        { category: 'POWER', name: 'Vertical Jump', bestValue: 38.5, percentile: 92 },
        { category: 'AGILITY', name: '3-Cone Drill', bestValue: 6.81, percentile: 90 },
        { category: 'POWER', name: 'Broad Jump', bestValue: 124, percentile: 87 },
      ]
    : [
        { category: 'SPEED', name: '60-Yard Dash', bestValue: 6.62, percentile: 94 },
        { category: 'POWER', name: 'Exit Velocity', bestValue: 98, percentile: 91 },
        { category: 'AGILITY', name: 'Pop Time', bestValue: 1.92, percentile: 88 },
      ]
}

/**
 * `GET /api/profile/:athleteId` (backend/src/routes/profile.ts:19).
 *
 * Everything is wrapped in `publicData`; there is no flat profile. The old
 * fixture returned the fields at the top level, mirroring the recruiting page's
 * interface rather than the route — so the demo looked right while a real
 * athlete rendered a blank name and the page crashed on `combineResults.map`
 * (#159). `combineStats` is keyed by drill NAME and carries no `unit`, because
 * the route does not select one.
 */
function publicProfile(athleteId: string) {
  const a = ATHLETES.find(x => x.id === athleteId) || ATHLETES[0]
  const drills = demoCombineDrills(a.sport)

  return {
    publicData: {
      firstName: a.firstName,
      lastName: a.lastName,
      school: a.school,
      graduationYear: a.graduationYear,
      height: demoHeightInches(a.height),
      weight: a.weight,
      positions: [{ sport: a.sport, position: a.position, isPrimary: true }],
      sport: a.sport,
      combineStats: Object.fromEntries(
        drills.map(d => [d.name, { bestResult: d.bestValue, percentile: d.percentile }])
      ),
      eventCount: 5,
    },
  }
}

/**
 * `GET /api/profile/:athleteId/stats` (backend/src/routes/profile.ts:194).
 *
 * Category rollups keyed `{ athleteId, stats }` — NOT the flat
 * `{ eventsAttended, overallPercentile, personalRecords, combineResults }` the
 * old fixture invented to match the page. `bestValue` is a number; the old
 * fixture sent strings.
 */
function profileStats(athleteId: string) {
  const a = ATHLETES.find(x => x.id === athleteId) || ATHLETES[0]
  const drills = demoCombineDrills(a.sport)

  const byCategory = new Map<string, typeof drills>()
  for (const drill of drills) {
    byCategory.set(drill.category, [...(byCategory.get(drill.category) || []), drill])
  }

  return {
    athleteId: a.id,
    stats: Array.from(byCategory.entries()).map(([category, entries]) => ({
      category,
      avgPercentile:
        Math.round((entries.reduce((sum, d) => sum + d.percentile, 0) / entries.length) * 100) /
        100,
      drills: entries.map(d => ({
        name: d.name,
        bestValue: d.bestValue,
        percentile: d.percentile,
      })),
    })),
  }
}

/**
 * `GET /api/drills` (backend/src/routes/drills.ts:60).
 *
 * `Drill.id` is a `cuid()`. CreateEvent used to ship a hardcoded catalogue with
 * ids '1'..'10', which could never match a real row — fabricated *input* that
 * had events rejected or persisted with dangling drill references (#159). These
 * ids are cuid-shaped so nothing positional creeps back in.
 */
function drillCatalogue() {
  const drills = [
    { name: '40 Yard Dash', sport: 'FOOTBALL', category: 'SPEED', unit: 'seconds' },
    { name: 'Vertical Jump', sport: 'FOOTBALL', category: 'POWER', unit: 'inches' },
    { name: 'Broad Jump', sport: 'FOOTBALL', category: 'POWER', unit: 'inches' },
    { name: '20 Yard Shuttle', sport: 'FOOTBALL', category: 'AGILITY', unit: 'seconds' },
    { name: '3 Cone Drill', sport: 'FOOTBALL', category: 'AGILITY', unit: 'seconds' },
    { name: '60 Yard Dash', sport: 'BASEBALL', category: 'SPEED', unit: 'seconds' },
    { name: 'Catcher Pop Time', sport: 'BASEBALL', category: 'SKILL', unit: 'seconds' },
    { name: 'Exit Velocity', sport: 'BASEBALL', category: 'POWER', unit: 'mph' },
  ].map((drill, index) => ({
    id: `cldrill${String(index).padStart(4, '0')}demo${index}`,
    name: drill.name,
    sport: drill.sport,
    category: drill.category,
    description: `${drill.name} — demo catalogue entry.`,
    instructions: 'Perform the drill as directed by the station operator.',
    measurementType: 'NUMERIC',
    unit: drill.unit,
    equipment: 'Standard combine equipment',
    videoUrl: null,
    isActive: true,
    createdAt: daysFromNow(-400),
    updatedAt: daysFromNow(-30),
  }))

  return {
    drills,
    pagination: { page: 1, limit: 100, total: drills.length, pages: 1 },
  }
}

function athleteClips() {
  return {
    clips: [
      {
        id: 'clip-1',
        title: '40-Yard Dash — 4.38s',
        description: 'Spring Showcase 2026',
        skillTag: 'speed',
        duration: 12,
        approved: true,
        thumbnailUrl: '',
      },
      {
        id: 'clip-2',
        title: 'Route Running — Sideline',
        description: 'Sharp out-cut at full speed',
        skillTag: 'agility',
        duration: 18,
        approved: true,
        thumbnailUrl: '',
      },
    ],
  }
}

// ---------------------------------------------------------------------------
// Training plan fixtures
// ---------------------------------------------------------------------------

function trainingPlans() {
  return {
    plans: [
      {
        id: 'plan-1',
        athleteId: 'ath-1',
        planType: 'performance',
        name: 'Off-Season Speed & Power',
        approved: true,
        createdAt: daysFromNow(-21),
      },
      {
        id: 'plan-2',
        athleteId: 'ath-1',
        planType: 'custom',
        name: 'Route-Running Explosiveness',
        approved: false,
        createdAt: daysFromNow(-5),
      },
    ],
  }
}

function trainingPlanDetail(planId: string) {
  return {
    id: planId,
    athleteId: 'ath-1',
    planType: 'performance',
    name: 'Off-Season Speed & Power',
    approved: true,
    approvedBy: 'Coach Bennett',
    approvedAt: daysFromNow(-20),
    createdAt: daysFromNow(-21),
    disclaimer: 'Demo training plan — for illustration only. Not medical advice.',
    plan: {
      name: 'Off-Season Speed & Power',
      description: 'A 4-week periodized block targeting acceleration and lower-body power.',
      phase: 'OFF_SEASON',
      durationWeeks: 4,
      disclaimer: 'Consult a certified strength coach before beginning any program.',
      citations: [
        'Haff & Triplett (2016). Essentials of Strength Training and Conditioning, 4th ed.',
        'NSCA Position Stand: Long-Term Athletic Development (2016).',
      ],
      weeks: [1, 2, 3, 4].map(weekNumber => ({
        weekNumber,
        focus: weekNumber <= 2 ? 'Acceleration & Max Strength' : 'Power Conversion & Speed',
        workouts: [
          {
            day: 'Monday',
            exercises: [
              {
                name: 'Back Squat',
                sets: 4,
                reps: '5',
                restSeconds: 180,
                category: 'strength',
                notes: 'Build to a heavy 5',
              },
              { name: 'Sled Push (10yd)', sets: 5, reps: '1', restSeconds: 120, category: 'speed' },
              { name: 'Box Jumps', sets: 4, reps: '3', restSeconds: 120, category: 'power' },
            ],
          },
          {
            day: 'Thursday',
            exercises: [
              {
                name: 'Trap-Bar Deadlift',
                sets: 4,
                reps: '4',
                restSeconds: 180,
                category: 'strength',
              },
              {
                name: 'Flying 20s',
                sets: 6,
                reps: '1',
                restSeconds: 150,
                category: 'speed',
                notes: 'Full recovery between reps',
              },
              { name: 'Broad Jumps', sets: 4, reps: '3', restSeconds: 120, category: 'power' },
            ],
          },
        ],
      })),
    },
  }
}

// ---------------------------------------------------------------------------
// Command center fixtures
// ---------------------------------------------------------------------------

// Mirrors GET /api/command-center/event/:id exactly
// (backend/src/routes/command-center.ts): `event.startDate` not `date`, `flow`
// keyed `totalRegistered` not `metrics`, and `topPerformers` keyed
// `{athleteId, name}`. This fixture was authored against the frontend's
// *interface* rather than the API, so it encoded the same wrong contract the
// component did — which is precisely why #155 reached the public demo.
function commandCenter() {
  return {
    event: {
      id: 'evt-cc-1',
      name: 'Spring Showcase 2026 — QB/WR Session',
      sport: 'FOOTBALL',
      status: 'ACTIVE',
      startDate: daysFromNow(0),
      endDate: daysFromNow(0),
    },
    flow: {
      totalRegistered: 23,
      checkedIn: 21,
      inProgress: 3,
      completed: 18,
      noShow: 2,
    },
    recentResults: ATHLETES.slice(0, 6).map((a, i) => ({
      id: `cc-res-${i}`,
      athleteName: athleteName(a),
      drill: '40-Yard Dash',
      value: Number((4.38 + i * 0.05).toFixed(2)),
      unit: 'SECONDS',
      percentile: a.topPercentile,
    })),
    topPerformers: ATHLETES.slice(0, 4).map((a, i) => ({
      athleteId: `cc-top-${i}`,
      name: athleteName(a),
      compositeScore: 95 - i * 4,
    })),
  }
}

// Mirrors GET /api/command-center/event/:id/stations
// (backend/src/routes/command-center.ts:281-289): `drillId`, a `queue` array of
// {athleteId, name}, `completedCount`/`averageValue`/`bestValue`, and `unit`.
// The previous fixture invented `id`, `queueDepth`, `currentAthlete`, `status`
// and a *string* `lastResult` — while the component's own interface declared
// `lastResult` as an object, so even the demo rendered that row wrong.
function commandCenterStations() {
  const queueOf = (n: number) =>
    ATHLETES.slice(0, n).map(a => ({ athleteId: a.id, name: athleteName(a) }))

  return {
    stations: [
      {
        drillId: 'st-1',
        drillName: '40-Yard Dash',
        unit: 'SECONDS',
        queue: queueOf(4),
        completedCount: 17,
        averageValue: 4.61,
        bestValue: 4.38,
      },
      {
        drillId: 'st-2',
        drillName: 'Vertical Jump',
        unit: 'INCHES',
        queue: queueOf(2),
        completedCount: 15,
        averageValue: 31.4,
        bestValue: 36.5,
      },
      {
        drillId: 'st-3',
        drillName: '3-Cone Drill',
        unit: 'SECONDS',
        queue: queueOf(6),
        completedCount: 11,
        averageValue: 7.18,
        bestValue: 6.82,
      },
      {
        drillId: 'st-4',
        drillName: 'Broad Jump',
        unit: 'INCHES',
        queue: [],
        completedCount: 0,
        averageValue: 0,
        bestValue: 0,
      },
    ],
  }
}

// ---------------------------------------------------------------------------
// Organization hierarchy fixtures
// ---------------------------------------------------------------------------

function organizationHierarchy() {
  return {
    districts: [
      {
        id: 'dst-1',
        name: 'Northside ISD',
        code: 'NISD',
        type: 'PUBLIC',
        city: 'San Antonio',
        state: 'TX',
        superintendent: 'Dr. Lana Whitfield',
        phone: '(555) 200-1000',
        schools: [
          {
            id: 'sch-1',
            name: 'Westfield High',
            code: 'WHS',
            type: 'HIGH_SCHOOL',
            enrollment: 2100,
            gradeRange: '9-12',
            principal: 'Mr. Owens',
            feederSchools: [{ id: 'sch-ms-1', name: 'Westfield Middle', feedingLevel: 'MIDDLE' }],
            programs: [
              {
                id: 'prg-1',
                name: 'Varsity Football',
                sport: 'FOOTBALL',
                level: 'VARSITY',
                isActive: true,
                headCoach: { id: 'coach-1', profile: { firstName: 'Marcus', lastName: 'Bennett' } },
                teams: [
                  {
                    id: 'team-1',
                    name: 'Westfield Varsity',
                    level: 'VARSITY',
                    isActive: true,
                    maxPlayers: 53,
                    playerCount: 48,
                    program: {
                      id: 'prg-1',
                      name: 'Varsity Football',
                      sport: 'FOOTBALL',
                      school: { id: 'sch-1', name: 'Westfield High' },
                    },
                  },
                ],
              },
              {
                id: 'prg-1b',
                name: 'Varsity Baseball',
                sport: 'BASEBALL',
                level: 'VARSITY',
                isActive: true,
                teams: [],
              },
            ],
          },
          {
            id: 'sch-2',
            name: 'Northgate Academy',
            code: 'NGA',
            type: 'HIGH_SCHOOL',
            enrollment: 1450,
            gradeRange: '9-12',
            principal: 'Ms. Patel',
            feederSchools: [],
            programs: [
              {
                id: 'prg-2',
                name: 'Varsity Football',
                sport: 'FOOTBALL',
                level: 'VARSITY',
                isActive: true,
                teams: [],
              },
            ],
          },
          {
            id: 'sch-3',
            name: 'Riverside High',
            code: 'RHS',
            type: 'HIGH_SCHOOL',
            enrollment: 1820,
            gradeRange: '9-12',
            principal: 'Mr. Cole',
            feederSchools: [],
            programs: [
              {
                id: 'prg-3',
                name: 'Varsity Baseball',
                sport: 'BASEBALL',
                level: 'VARSITY',
                isActive: true,
                teams: [],
              },
            ],
          },
        ],
      },
    ],
  }
}

// ---------------------------------------------------------------------------
// Resolver
// ---------------------------------------------------------------------------

/**
 * Map an API endpoint to demo data. Returns the bare `data` payload (the API
 * service wraps it as { data }). Unmatched endpoints fall through to the
 * `{ __demoUnhandled: true }` sentinel so an uncovered endpoint is detectable
 * (rather than silently looking like a successful empty response).
 */
export function resolveDemoRequest(endpoint: string, method: string, body?: unknown): unknown {
  const path = endpoint.split('?')[0]
  const verb = method.toUpperCase()

  // --- Auth ---
  if (path === '/auth/login') {
    return buildAuthResponse()
  }
  if (path === '/auth/logout') {
    return {}
  }
  if (path === '/scopes/my-scopes') {
    const role = getDemoRole()
    const scopes: Array<{
      id: string
      scopeType: string
      scopeId: string | null
      isActive: boolean
    }> = []
    if (role === 'EVENT_ORGANIZER' || role === 'ADMIN') {
      scopes.push({ id: 'scope-1', scopeType: 'STATION_OPERATOR', scopeId: null, isActive: true })
    }
    if (role === 'ADMIN') {
      scopes.push({ id: 'scope-2', scopeType: 'DISTRICT', scopeId: 'dst-1', isActive: true })
    }
    // Envelope must match the real backend (`return { scopes }` in
    // backend/src/routes/scopes.ts). Returning a bare array here is what let
    // the demo path work while the real path was broken (#154).
    return { scopes }
  }

  // --- Events ---
  if (path === '/events' && verb === 'GET') {
    return eventsList
  }
  if (path === '/events' && verb === 'POST') {
    const b = (body as Record<string, unknown>) || {}
    return {
      ...EVENTS[0],
      id: `evt-new-${Date.now()}`,
      name: (b.name as string) || 'New Event',
      status: 'DRAFT',
      participantCount: 0,
      participants: [],
    }
  }
  const eventDetail = path.match(/^\/events\/([^/]+)$/)
  if (eventDetail) {
    if (verb === 'DELETE') {
      return {}
    }
    if (verb === 'PUT') {
      const ev = EVENTS.find(e => e.id === eventDetail[1]) || EVENTS[0]
      return { ...ev, ...((body as object) || {}) }
    }
    return EVENTS.find(e => e.id === eventDetail[1]) || EVENTS[0]
  }
  const participants = path.match(/^\/events\/([^/]+)\/participants$/)
  if (participants) {
    const ev = EVENTS.find(e => e.id === participants[1]) || EVENTS[0]
    return { participants: ev.participants }
  }
  const checkin = path.match(/^\/events\/[^/]+\/checkin\/([^/]+)$/)
  if (checkin) {
    return { id: checkin[1], status: 'CHECKED_IN', checkedInAt: new Date().toISOString() }
  }

  // --- Analytics ---
  const athAnalytics = path.match(/^\/analytics\/athletes\/([^/?]+)/)
  if (athAnalytics) {
    return athleteAnalytics(athAnalytics[1])
  }
  const evtAnalytics = path.match(/^\/analytics\/events\/([^/]+)$/)
  if (evtAnalytics) {
    return eventAnalytics(evtAnalytics[1])
  }
  const orgAnalytics = path.match(/^\/analytics\/organizations\/([^/]+)\/([^/]+)$/)
  if (orgAnalytics) {
    return organizationAnalytics(orgAnalytics[1], orgAnalytics[2])
  }
  if (path.startsWith('/analytics/coach/overview')) {
    return coachOverview()
  }

  // --- Athletes ---
  // `/athletes/me` must be matched before the `/athletes/:id` shape below, and
  // mirrors the real route: it takes no id and resolves from the demo session.
  if (path === '/athletes/me' && verb === 'GET') {
    return myAthlete()
  }
  if (path.startsWith('/athletes') && !path.includes('/performance') && verb === 'GET') {
    const athleteDetail = path.match(/^\/athletes\/([^/?]+)$/)
    if (athleteDetail) {
      const found = ATHLETES.find(a => a.id === athleteDetail[1])
      if (found) {
        return athletesList().athletes.find(a => a.id === found.id)
      }
    }
    // `path` already has any query string stripped, so a filtered request
    // (`/athletes?search=…&limit=100`) lands here as a plain `/athletes`.
    if (path === '/athletes') {
      return athletesList()
    }
  }

  // --- Drills ---
  if (path === '/drills' && verb === 'GET') {
    return drillCatalogue()
  }

  // --- Monitoring ---
  if (path.startsWith('/monitoring/health')) {
    return systemHealth()
  }

  // --- Results ---
  if (path === '/results') {
    return resultsList()
  }

  // --- Scout ---
  if (path.startsWith('/scout/search')) {
    return scoutSearch()
  }
  if (path === '/scout/watchlist' && verb === 'GET') {
    return scoutWatchlist()
  }
  if (path === '/scout/watchlist' && verb === 'POST') {
    return { success: true }
  }
  if (path.startsWith('/scout/watchlist/') && verb === 'DELETE') {
    return {}
  }
  if (path.startsWith('/scout/compare')) {
    // Read from `endpoint`, not `path` — `path` has the query string stripped.
    const ids = (endpoint.split('athleteIds=')[1] || '').split(',').filter(Boolean)
    return scoutCompare(ids.map(decodeURIComponent))
  }

  // --- Parent / consent ---
  if (path === '/consent/my-children') {
    return myChildren()
  }
  const perf = path.match(/^\/athletes\/([^/]+)\/performance$/)
  if (perf) {
    return athletePerformance(perf[1])
  }

  // --- Public profile ---
  const profStats = path.match(/^\/profile\/([^/]+)\/stats$/)
  if (profStats) {
    return profileStats(profStats[1])
  }
  const prof = path.match(/^\/profile\/([^/]+)$/)
  if (prof) {
    return publicProfile(prof[1])
  }

  // --- Video ---
  const clips = path.match(/^\/video\/athlete\/([^/]+)$/)
  if (clips) {
    return athleteClips()
  }
  const tags = path.match(/^\/video\/tags\/([^/]+)$/)
  if (tags) {
    return { sport: tags[1], tags: ['speed', 'agility', 'power', 'technique', 'highlight'] }
  }

  // --- Training ---
  if (path === '/training/generate') {
    return trainingPlanDetail(`plan-${Date.now()}`)
  }
  const planDetail = path.match(/^\/training\/plans\/([^/]+)\/detail$/)
  if (planDetail) {
    return trainingPlanDetail(planDetail[1])
  }
  const planApprove = path.match(/^\/training\/plans\/([^/]+)\/approve$/)
  if (planApprove) {
    return { id: planApprove[1], approved: true }
  }
  const planExport = path.match(/^\/training\/plans\/([^/]+)\/export$/)
  if (planExport) {
    return { url: '#', message: 'Export available in full version' }
  }
  const plans = path.match(/^\/training\/plans\/([^/]+)$/)
  if (plans) {
    return trainingPlans()
  }

  // --- Command center ---
  const ccStations = path.match(/^\/command-center\/event\/([^/]+)\/stations$/)
  if (ccStations) {
    return commandCenterStations()
  }
  const ccValidate = path.match(/^\/command-center\/event\/[^/]+\/validate\/([^/]+)$/)
  if (ccValidate) {
    return { id: ccValidate[1], status: 'VALIDATED' }
  }
  const cc = path.match(/^\/command-center\/event\/([^/]+)$/)
  if (cc) {
    return commandCenter()
  }

  // --- Organizations ---
  if (path.startsWith('/organizations/hierarchy')) {
    return organizationHierarchy()
  }
  if (path.startsWith('/organizations/navigation')) {
    return { navigationTree: [] }
  }

  // Fallback sentinel for uncovered endpoints.
  return { __demoUnhandled: true }
}

/**
 * Roster for the Athletes page, which calls this DIRECTLY rather than through
 * `resolveDemoRequest` — so the `/athletes` contract guard does not cover it.
 *
 * It used to emit `recentPerformance` with an `averagePercentile` and per-drill
 * percentiles, rendered as "{n}th percentile" badges. `GET /api/athletes`
 * declares no performance data at all, so those badges could never appear for a
 * real user: the demo was advertising a screen the product cannot produce, and
 * the guard asserting `not.toHaveProperty('recentPerformance')` on the resolver
 * was green over a path this page never takes (#159).
 */
export function demoAthleteRoster() {
  const positionLabel: Record<string, string> = {
    QB: 'Quarterback',
    RB: 'Running Back',
    WR: 'Wide Receiver',
    CB: 'Defensive Back',
    LB: 'Linebacker',
    S: 'Defensive Back',
    C: 'Catcher',
    SS: 'Infielder',
    CF: 'Outfielder',
    P: 'Pitcher',
    '2B': 'Infielder',
  }
  const heightInches = (h: string) => {
    const m = h.match(/(\d+)'(\d+)/)
    return m ? parseInt(m[1], 10) * 12 + parseInt(m[2], 10) : 72
  }
  return ATHLETES.map(a => ({
    id: a.id,
    email: `${a.firstName.toLowerCase()}.${a.lastName.toLowerCase()}@demo.ecombine.app`,
    profile: {
      firstName: a.firstName,
      lastName: a.lastName,
      position: positionLabel[a.position] || a.position,
      height: heightInches(a.height),
      weight: a.weight,
      graduationYear: a.graduationYear,
    },
    teams: [
      {
        id: `team-${a.id}`,
        name: `${a.school} Varsity`,
        program: {
          name: `${a.school} ${a.sport === 'FOOTBALL' ? 'Football' : 'Baseball'}`,
          sport: a.sport,
          school: { name: a.school, district: { name: 'Northside ISD' } },
        },
      },
    ],
    createdAt: daysFromNow(-200),
  }))
}

// Flat rows shaped for the Results page table.
export function demoResultsRows() {
  const rows: Array<{
    id: string
    athleteName: string
    position: string
    school: string
    sport: string
    drill: string
    value: string
    unit: string
    percentile: number
    verified: boolean
    event: string
    recordedAt: string
  }> = []
  ATHLETES.forEach((a, i) => {
    const isFootball = a.sport === 'FOOTBALL'
    const drills = isFootball
      ? [
          { drill: '40-Yard Dash', value: (4.38 + i * 0.03).toFixed(2), unit: 's' },
          { drill: 'Vertical Jump', value: String(40 - i), unit: 'in' },
        ]
      : [
          { drill: '60-Yard Dash', value: (6.6 + i * 0.04).toFixed(2), unit: 's' },
          { drill: 'Exit Velocity', value: String(98 - i), unit: 'mph' },
        ]
    drills.forEach((d, j) => {
      rows.push({
        id: `row-${a.id}-${j}`,
        athleteName: athleteName(a),
        position: a.position,
        school: a.school,
        sport: a.sport,
        drill: d.drill,
        value: d.value,
        unit: d.unit,
        percentile: Math.max(58, a.topPercentile - j * 4),
        verified: (i + j) % 4 !== 0,
        event: isFootball ? 'Spring Showcase 2026' : 'Diamond Combine 2026',
        recordedAt: minsAgo(i * 9 + j * 3 + 2),
      })
    })
  })
  return rows
}

// Simulated live drill-result stream for RealTimeResults in demo mode.
export function demoDrillResultStream() {
  return ATHLETES.slice(0, 6).map((a, i) => ({
    id: `live-${a.id}`,
    value:
      a.sport === 'FOOTBALL'
        ? Number((4.38 + i * 0.05).toFixed(2))
        : Number((6.62 + i * 0.05).toFixed(2)),
    unit: 's',
    athlete: { id: a.id, name: athleteName(a) },
    drill: {
      id: 'drl-40',
      name: a.sport === 'FOOTBALL' ? '40-Yard Dash' : '60-Yard Dash',
      sport: a.sport,
    },
    timestamp: minsAgo(i * 2 + 1),
  }))
}
